import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, AlertTriangle } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Motion";
import { DOCTOR } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";
import { useContent } from "@/i18n/useContent";

const SECTION_KEYS = {
  "Overview": ["sec_overview", "sec_overview_q"],
  "Symptoms": ["sec_symptoms", "sec_symptoms_q"],
  "Diagnosis": ["sec_diagnosis", "sec_diagnosis_q"],
  "Treatment": ["sec_treatment", "sec_treatment_q"],
  "Recovery": ["sec_recovery", "sec_recovery_q"],
};

const Section = ({ eyebrow, title, children, testId }) => {
  const { t } = useLang();
  const [eyebrowKey, titleKey] = SECTION_KEYS[eyebrow] || [null, null];
  return (
    <Reveal>
      <section data-testid={testId} className="py-10 border-t border-pine-900/15 first:border-t-0 first:pt-0">
        <h2 className="text-xs uppercase tracking-[0.25em] font-semibold text-pine-500">
          {eyebrowKey ? t(eyebrowKey) : eyebrow}
        </h2>
        <h3 className="mt-3 font-serif font-light text-3xl tracking-tight text-pine-950">
          {titleKey ? t(titleKey) : title}
        </h3>
        <div className="mt-4 text-sage leading-relaxed space-y-4">{children}</div>
      </section>
    </Reveal>
  );
};

const ConditionDetail = () => {
  const { t, lang } = useLang();
  const { conditions: CONDITIONS_L } = useContent();
  const { slug } = useParams();
  const condition = CONDITIONS_L.find((c) => c.slug === slug);
  const idx = CONDITIONS_L.findIndex((c) => c.slug === slug);
  const next = CONDITIONS_L[(idx + 1) % CONDITIONS_L.length];

  if (!condition) {
    return (
      <main className="min-h-screen bg-bone flex items-center justify-center px-6" data-testid="condition-not-found">
        <div className="text-center">
          <h1 className="font-serif font-light text-5xl text-pine-950">{t("gd_notfound")}</h1>
          <Link
            to="/conditions"
            className="mt-6 inline-flex items-center gap-2 font-semibold text-sm text-pine-900 hover:text-pine-500 transition-colors"
          >
            <ArrowLeft size={16} /> {t("gd_back")}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="condition-detail-page">
      <PageHero
        key={lang}
        eyebrow={`${t("gd_prefix")} ${String(idx + 1).padStart(2, "0")} ${t("gd_of")} ${String(CONDITIONS_L.length).padStart(2, "0")}`}
        lines={[condition.title]}
        testId="condition-hero"
      >
        {condition.tagline}
      </PageHero>

      <div className="bg-bone pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-8">
            <Section eyebrow="Overview" title="What is it?" testId="condition-overview">
              <p>{condition.overview}</p>
              <p>{condition.overview2}</p>
            </Section>

            <Section eyebrow="Symptoms" title="What does it feel like?" testId="condition-symptoms">
              <ul className="space-y-3" data-testid="symptoms-list">
                {condition.symptoms.map((s) => (
                  <li key={s} className="flex items-start gap-3">
                    <Check size={17} className="mt-1 shrink-0 text-pine-500" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section eyebrow="Diagnosis" title="How is it diagnosed?" testId="condition-diagnosis">
              <p>{condition.diagnosis}</p>
            </Section>

            <Section eyebrow="Treatment" title="How is it treated?" testId="condition-treatment">
              <p>{condition.treatment}</p>
            </Section>

            <Section eyebrow="Recovery" title="What does recovery look like?" testId="condition-recovery">
              <p>{condition.recovery}</p>
            </Section>

            <Reveal>
              <div
                data-testid="urgent-care-note"
                className="mt-10 bg-pine-950 text-bone p-8 lg:p-10 flex items-start gap-5"
              >
                <AlertTriangle size={26} className="shrink-0 text-pine-300 mt-1" />
                <div>
                  <h3 className="font-serif text-2xl">{t("gd_urgent_title")}</h3>
                  <p className="mt-3 text-sm text-pine-100/85 leading-relaxed">{condition.whenToSeek}</p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-4">
            <Reveal delay={0.15}>
              <div className="lg:sticky lg:top-32 space-y-6">
                <div className="bg-white border border-mist p-8" data-testid="condition-cta-card">
                  <Eyebrow>{t("gd_cta_eyebrow")}</Eyebrow>
                  <p className="mt-4 font-serif text-2xl text-pine-950 leading-snug">
                    {t("gd_cta_title")}
                  </p>
                  <p className="mt-3 text-sm text-sage leading-relaxed">
                    {t("gd_cta_body")}
                  </p>
                  <Link
                    to="/book"
                    data-testid="condition-book-button"
                    className="group mt-6 w-full inline-flex items-center justify-center gap-2 bg-pine text-bone font-semibold text-sm px-6 py-4 rounded-full hover:bg-pine-500 transition-colors duration-300"
                  >
                    {t("gd_cta_book")}
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <a
                    href={DOCTOR.phoneHref}
                    data-testid="condition-call-link"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-pine-900/20 text-pine-900 font-semibold text-sm px-6 py-4 rounded-full hover:border-pine-500 hover:text-pine-500 transition-colors duration-300"
                  >
                    {t("hero_call", { phone: DOCTOR.phoneDisplay })}
                  </a>
                </div>

                <Link
                  to={`/conditions/${next.slug}`}
                  data-testid="next-condition-link"
                  className="group flex items-center justify-between gap-4 bg-pine-50 border border-pine-100 p-6 hover:bg-pine-100 transition-colors duration-300"
                >
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.22em] text-sage">{t("gd_next")}</span>
                    <span className="block mt-1 font-serif text-xl text-pine-950">{next.title}</span>
                  </span>
                  <ArrowRight size={18} className="shrink-0 text-pine-400 group-hover:translate-x-1 group-hover:text-pine-500 transition-[color,transform] duration-300" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ConditionDetail;
